import { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "app/services/auth-services";

export const useDocumentData = (params) => {
  const [requests, setRequests] = useState({});
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      // Don't fetch if params are not ready
      if (!params || !params.WorkflowName || !params.Id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // 1. Fetch the main document data
        const documentData = await fetchDocumentData(
          params.WorkflowName,
          params.Id,
        );
        setRequests(Array.isArray(documentData) ? documentData : documentData);

        // 2. Fetch approval workflow status (Materialrequestworkflow)
        if (documentData) {
          await fetchApprovalWorkflow(
            params.WorkflowName,
            documentData,
            params.numberIndex,
          );
        }

        // 3. Fetch update history
        if (
          documentData &&
          params.WorkflowName !== "Attandance Management" &&
          params.WorkflowName !== "payroll management"
        ) {
          await fetchUpdateHistory(params.WorkflowName, documentData);
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [params?.WorkflowName, params?.Id, params?.numberIndex]);

  const fetchDocumentData = async (workflowName, id) => {
    const API_ENDPOINTS = {
      Leave: "/Felix/Getleave/",
      "Leave Cancellation": "/Felix/LeavecancellationwithouttokenCRUD/",
      timesheet: "/Felix/TimeSheetwithoutCRUD/",
      TimesheetLog: "/Felix/ListOfTimesheetsGetById/",
      Reimbursement: "/Felix/EmployeeClaimGetById/",
      "Full and Final Settlement": "/Felix/FullandFinalCRUD/",
      Resignation: "/Felix/EmployeeResignationGet/",
      "Attandance Management": "/Felix/ListOfAttandanceGetById/",
      "payroll management": "/Felix/ListOfPayslipGetById/",
    };

    try {
      if (
        workflowName === "Attandance Management" ||
        workflowName === "payroll management" ||
        workflowName === "TimesheetLog"
      ) {
        return await fetchMultipleDocuments(workflowName, id);
      }

      const endpoint = API_ENDPOINTS[workflowName];
      if (!endpoint) throw new Error(`Unknown workflow: ${workflowName}`);

      const response = await axios.get(`${BASE_URL}${endpoint}?id=${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${workflowName} data:`, error);
      throw error;
    }
  };

  const fetchMultipleDocuments = async (workflowName, id) => {
    const API_ENDPOINTS = {
      "Attandance Management": "/Felix/ListOfAttandanceGetById/",
      "payroll management": "/Felix/ListOfPayslipGetById/",
      TimesheetLog: "/Felix/ListOfTimesheetsGetById/",
    };

    const endpoint = API_ENDPOINTS[workflowName];

    // ✅ SAFE parsing
    const idArray = Array.isArray(id)
      ? id
      : JSON.parse(id.replace(/'/g, '"'));

    console.log("ID ARRAY:", idArray);

    const response = await axios.post(
      `${BASE_URL}${endpoint}`,
      { ids: idArray },
      { headers: { "Content-Type": "application/json" } }
    );

    return response.data?.count > 0 ? response?.data?.results : response?.data || [];
  };

  const fetchApprovalWorkflow = async (
    workflowName,
    documentData,
    numberIndex,
  ) => {
    try {
      let requestId = "";

      // Determine the request ID based on workflow type
      switch (workflowName) {
        case "Leave":
          requestId = documentData?.Application_Id;
          break;
        case "Leave Cancellation":
          requestId = documentData?.Cancellation_Id;
          break;
        case "timesheet":
          requestId = documentData?.TimeSheet_Id;
          break;
        case "Reimbursement":
          requestId = documentData?.Employeeclaim_Id;
          break;
        case "Full and Final Settlement":
          requestId = documentData?.FullandFinal_Id;
          break;
        case "Resignation":
          requestId = documentData?.EmployeeResignation_Id;
          break;
        case "Attandance Management":
          requestId = documentData?.[0]?.Attandancemaster_Id;
          break;
        case "payroll management":
          requestId = documentData?.[0]?.Payslip_Id;
          break;
        case "TimesheetLog":
          requestId = documentData?.[0]?.TimeSheet_Id;
          break;
        default:
          return;
      }

      if (!requestId) return;

      const response = await axios.get(
        `${BASE_URL}/Felix/Materialrequestworkflow/?Request_Id=${requestId}`,
      );

      // const workflowData =
      //   workflowName === "TimesheetLog"
      //     ? {
      //         Approve: [
      //           {
      //             id: 43,
      //             time: "",
      //             status: false,
      //             comments: "",
      //             status_btn: "",
      //             Approved_flg: false,
      //             ApproverRole: "CEO",
      //             Approvername: "N R Patnaik ",
      //             approved_flg: "",
      //           },
      //         ],
      //       }
      //     : response?.data?.[0];

      const workflowData = response?.data?.[0];
      setApprovalStatus(workflowData);

      // If numberIndex is not 0, update approvals from workflow data
      if (numberIndex !== 0 && workflowData?.Approve) {
        // This logic can be handled in the parent component if needed
      }
    } catch (error) {
      console.error("Error fetching approval workflow:", error);
    }
  };

  const fetchUpdateHistory = async (workflowName, documentData) => {
    try {
      const token = localStorage.getItem("accesstoken");
      const header = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      };

      let partnerId = documentData?.Partner_Id?.id || documentData?.Partner_Id;
      let documentId = "";

      // Determine document ID for history
      switch (workflowName) {
        case "Leave":
          documentId = documentData?.Application_Id;
          break;
        case "Leave Cancellation":
          documentId = documentData?.Cancellation_Id;
          break;
        case "timesheet":
          documentId = documentData?.TimeSheet_Id;
          break;
        case "Full and Final Settlement":
          documentId = documentData?.FullandFinal_Id;
          break;
        default:
          return;
      }

      if (!partnerId || !documentId) return;

      const response = await axios.get(
        `${BASE_URL}/Felix/Updaterecordsfilter/?Partner_Id=${partnerId}&Document_Id=${documentId}`,
        header,
      );

      setUpdateHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching update history:", error);
      // Don't throw error for history fetch - it's optional
    }
  };

  return {
    requests,
    approvalStatus,
    updateHistory,
    isLoading,
    error,
  };
};
